import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, FileSpreadsheet, Upload, Loader2, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

/**
 * DatasetUploadModal — used inside the JobDetailModal whenever the
 * client is about to accept an ARIA-Analyst bid (or wants to upgrade
 * the dataset attached to a job that's already mid-flight).
 *
 * Flow:
 *   1. Caller mounts with `jobId` + optional `existingDataset`.
 *   2. User drops/picks a CSV / XLSX.
 *   3. We POST to `/marketplace/jobs/{id}/dataset` (multipart).
 *   4. Backend profiles it inline; we render the column list + sample
 *      rows so the user can confirm before continuing.
 *   5. `onUploaded(dataset)` is fired so the caller can chain into
 *      accept-bid (or close the modal silently).
 *
 * Props:
 *   • jobId
 *   • existingDataset
 *   • onUploaded(dataset) — confirmation callback
 *   • onClose()
 */
export default function DatasetUploadModal({ jobId, existingDataset, onUploaded, onClose }) {
  const [dataset, setDataset] = useState(existingDataset || null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);
  const dragDepth = useRef(0);

  useEffect(() => { setDataset(existingDataset || null); }, [existingDataset]);

  const upload = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["csv", "xlsx", "xls"].includes(ext)) {
      toast.error("Dataset must be .csv, .xls, or .xlsx");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Dataset too large (max 20 MB)");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(
        `/marketplace/jobs/${jobId}/dataset`, form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDataset(res.data.dataset);
      toast.success(`Dataset uploaded — ${res.data.dataset.rows.toLocaleString()} rows × ${res.data.dataset.col_count} cols`);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't upload dataset"));
    } finally { setBusy(false); }
  };

  const removeDataset = async () => {
    if (!confirm("Remove the attached dataset?")) return;
    setBusy(true);
    try {
      await api.delete(`/marketplace/jobs/${jobId}/dataset`);
      setDataset(null);
      toast.success("Dataset removed");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't remove dataset"));
    } finally { setBusy(false); }
  };

  const confirm_ = () => {
    if (!dataset) {
      toast.error("Upload a dataset first");
      return;
    }
    onUploaded?.(dataset);
  };

  return (
    <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={() => !busy && onClose?.()}>
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="dataset-upload-modal"
        className="w-full max-w-2xl bg-luxury-black border border-gold/30 rounded-lg p-6 space-y-4 my-8"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold">CartBlancheJA · ARIA-Analyst</p>
            <h3 className="font-heading text-xl text-white mt-1">Attach your dataset</h3>
            <p className="text-[11px] text-white/55 mt-1">
              CSV / XLSX up to 20 MB. We profile it locally then ship a cleaned CSV + a markdown insights report.
            </p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="dataset-close-btn"><X size={20} /></button>
        </div>

        {/* DROPZONE */}
        <div
          data-testid="dataset-dropzone"
          onDragEnter={(e) => { e.preventDefault(); dragDepth.current++; setDragging(true); }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragging(false); }}
          onDrop={(e) => {
            e.preventDefault(); dragDepth.current = 0; setDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) upload(f);
          }}
          onClick={() => !busy && fileInput.current?.click()}
          className={`border-2 border-dashed rounded-md py-8 text-center transition-colors cursor-pointer ${
            dragging
              ? "border-gold bg-gold/10"
              : "border-white/15 hover:border-gold/40 bg-black/30"
          }`}
        >
          <input ref={fileInput} type="file" accept=".csv,.xlsx,.xls"
            data-testid="dataset-file-input"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden" />
          {busy ? (
            <div className="flex flex-col items-center gap-2 text-gold/80">
              <Loader2 size={28} className="animate-spin" />
              <p className="text-xs">Uploading + profiling…</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-white/55">
              <Upload size={26} className="text-gold/70" />
              <p className="text-sm">Drop a CSV or XLSX here</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">or click to browse</p>
            </div>
          )}
        </div>

        {/* PROFILE PREVIEW */}
        {dataset && (
          <div data-testid="dataset-preview" className="border border-white/10 rounded-md p-3 bg-black/30 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <FileSpreadsheet size={18} className="text-gold flex-shrink-0" />
                <p className="text-sm text-white truncate" data-testid="dataset-name">{dataset.name}</p>
                <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />
              </div>
              <button onClick={removeDataset} disabled={busy}
                data-testid="dataset-remove-btn"
                className="text-rose-300/70 hover:text-rose-300 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                <Trash2 size={11} /> Remove
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="p-2 bg-black/40 border border-white/10 rounded">
                <p className="text-[9px] uppercase tracking-wider text-white/40">Rows</p>
                <p className="text-gold font-display text-base">{dataset.rows?.toLocaleString() || "—"}</p>
              </div>
              <div className="p-2 bg-black/40 border border-white/10 rounded">
                <p className="text-[9px] uppercase tracking-wider text-white/40">Columns</p>
                <p className="text-gold font-display text-base">{dataset.col_count}</p>
              </div>
              <div className="p-2 bg-black/40 border border-white/10 rounded">
                <p className="text-[9px] uppercase tracking-wider text-white/40">Size</p>
                <p className="text-white font-display text-base">{Math.round((dataset.size_bytes || 0) / 1024)} KB</p>
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gold mb-1">Columns</p>
              <div className="flex flex-wrap gap-1">
                {(dataset.columns || []).slice(0, 30).map((c) => (
                  <span key={c} className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-white/65 font-mono">
                    {c}
                  </span>
                ))}
              </div>
            </div>
            {(dataset.sample_rows || []).length > 0 && (
              <div className="overflow-x-auto">
                <p className="text-[10px] uppercase tracking-wider text-gold mb-1">Sample rows</p>
                <table className="text-[10px] text-white/60 w-full border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      {(dataset.columns || []).slice(0, 6).map((c) => (
                        <th key={c} className="text-left px-1.5 py-1 font-mono text-gold/70">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(dataset.sample_rows || []).slice(0, 3).map((r, i) => (
                      <tr key={i} className="border-b border-white/5">
                        {(dataset.columns || []).slice(0, 6).map((c) => (
                          <td key={c} className="px-1.5 py-1 truncate max-w-[120px]">{r[c] ?? "—"}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy}
            data-testid="dataset-cancel-btn"
            className="px-4 py-3 border border-white/15 text-white/60 hover:text-white text-xs font-body uppercase tracking-wider rounded">
            Cancel
          </button>
          <button onClick={confirm_} disabled={busy || !dataset}
            data-testid="dataset-confirm-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 py-3 bg-gold text-luxury-black text-xs font-body uppercase tracking-wider rounded hover:bg-gold/90 disabled:opacity-40">
            <CheckCircle2 size={14} /> Continue
          </button>
        </div>
      </motion.div>
    </div>
  );
}
